import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { type Notification, getNotificationMeta, NAVIGABLE_TYPES } from '../../hooks/useNotifications';

interface NotificationToastProps {
  notification: Notification | null;
  onDismiss: () => void;
  onMarkRead: (id: string) => void;
}

export const NotificationToast = ({ notification, onDismiss, onMarkRead }: NotificationToastProps) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = () => {
    setExiting(true);
    setTimeout(() => {
      setExiting(false);
      setVisible(false);
      onDismiss();
    }, 300);
  };

  useEffect(() => {
    if (!notification) return;
    setVisible(true);
    setExiting(false);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      dismiss();
    }, 6000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification?.id]);

  if (!notification || !visible) return null;

  const meta = getNotificationMeta(notification.type);
  const isNavigable = NAVIGABLE_TYPES.includes(notification.type);

  const handleView = () => {
    onMarkRead(notification.id);
    dismiss();
    navigate('/notificacoes');
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  if (isMobile) return null;

  return (
    <div
      className={`fixed bottom-6 right-6 z-[9999] max-w-[340px] w-full pointer-events-auto transition-all duration-300 ${
        exiting
          ? 'opacity-0 translate-y-2 scale-95'
          : 'opacity-100 translate-y-0 scale-100'
      }`}
    >
      <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
        {/* Progress bar */}
        <div className="h-0.5 bg-gray-100 w-full">
          <div
            className="h-full bg-emerald-400 transition-all"
            style={{
              width: '100%',
              animation: 'toast-shrink 6s linear forwards',
            }}
          />
        </div>

        <div className="flex gap-3 px-4 py-3.5">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${meta.bg}`}>
            <i className={`${meta.icon} text-sm ${meta.color}`} />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-700 text-gray-900 leading-snug truncate">
              {notification.title}
            </p>
            {notification.body && (
              <p className="text-[12px] text-gray-500 mt-0.5 leading-snug line-clamp-2">
                {notification.body}
              </p>
            )}
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={handleView}
                className="text-[11px] font-700 text-emerald-600 hover:text-emerald-700 cursor-pointer whitespace-nowrap transition-colors"
              >
                Ver notificação →
              </button>
            </div>
          </div>

          <button
            onClick={dismiss}
            className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors flex-shrink-0 mt-0.5"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes toast-shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};
